import { FragmentType } from '@/__generated__';
import { IMAGE_FRAGMENT } from '@/gql/fragments/images.gql';
import { parseLinkField } from '@/lib/parsers/common';
import { Sprinkles } from '@/theme/sprinkles.css';
import { mergePropsClassName } from '@liquorice/allsorts-craftcms-nextjs';
import { withFragmentArray } from '@liquorice/gql-utils';
import {
  firstNonNullable,
  isTypedElement,
  makeNonNullableArray,
  toBoolean,
  toString,
} from '@liquorice/utils';
import NextImage, { ImageProps } from 'next/image';
import Shim, { ShimProps } from '../Shim';
import Box from '../ui/Box';
import Btn from '../ui/Btn';
import Txt from '../ui/Txt';
import * as styles from './Image.css';
import CardMaskImage from './Image.Mask';

export type ImageOwnProps = OverrideProps<
  Partial<ImageProps>,
  {
    ShimProps?: ShimProps;
    ratio?: Sprinkles['ratio'];
    rounded?: boolean;
    captions?: boolean;
    useFocalPoint?: boolean;
    // transform?: ResponsiveImageTransform;
    mask?: boolean;
    link?: boolean;
  } & styles.ImageVariants
>;

export type ImageAssetProps = ImageOwnProps & {
  data?: MaybeImage;
};

export type MaybeImage = MaybeArrayOf<FragmentType<typeof IMAGE_FRAGMENT>>;

const Image = withFragmentArray(IMAGE_FRAGMENT, (images, props: ImageOwnProps) => {
  const {
    ratio,
    ShimProps: maybeShimProps,
    fixedRatio: maybeFixedRatio,
    contain,
    rounded,
    round,
    mask = false,
    link = false,
    useFocalPoint = false,
    captions,
    style,
    ...rest
  } = props;

  const image = firstNonNullable(images);

  if (!isTypedElement(image, 'assetsVolume_Asset')) return null;

  if (!image.url) return null;

  if (mask) return <CardMaskImage src={image.url} title={toString(image.uid)} {...rest} />;

  const linkField = parseLinkField(image.linkField);
  const hasFocalPoint = toBoolean(image.hasFocalPoint) && useFocalPoint;
  const focalPoint = makeNonNullableArray<number>(image.focalPoint);

  const objectPosition =
    hasFocalPoint && focalPoint.length === 2
      ? `${focalPoint[0] * 100}% ${focalPoint[1] * 100}%`
      : undefined;

  /**
   * Create the Shim
   */
  const ShimProps: ShimProps<TagName> | undefined =
    maybeShimProps || ratio || rounded || round
      ? (mergePropsClassName(
          { ratio, round, rounded, ...maybeShimProps },
          styles.shim
        ) as ShimProps<TagName>)
      : undefined;

  const fixedRatio = maybeFixedRatio ?? !!ShimProps;

  const asset = (
    <NextImage
      {...{
        src: image.url,
        alt: image.alt ?? '',
        height: image.height ?? 100,
        width: image.width ?? 100,
        style: { objectPosition, ...style },
        ...mergePropsClassName(
          rest,
          styles.root({
            fixedRatio,
            contain,
          })
        ),
      }}
    />
  );

  const img = (
    <>
      {ShimProps ? <Shim {...ShimProps}>{asset}</Shim> : asset}
      {(image.caption || captions) && (
        <Box>
          <Txt color="black" as="figcaption" cx={{ mT: 'md' }}>
            {image.caption || image.alt}
          </Txt>
        </Box>
      )}
    </>
  );

  if (link && linkField?.href)
    return (
      <Btn as="a" href={linkField.href} target={linkField.target} variant="text">
        {img}
      </Btn>
    );

  return img;
});

export default Image;
